import type React from "react";
import type { IconBaseProps, IconType } from "react-icons";
import { FiArrowUpRight, FiClock, FiMapPin, FiPhoneCall } from "react-icons/fi";
import { SHOP_INFO } from "../config";

const asIconComponent = (Icon: IconType) =>
  Icon as unknown as React.FC<IconBaseProps>;

const PhoneIcon = asIconComponent(FiPhoneCall);
const ClockIcon = asIconComponent(FiClock);
const MapPinIcon = asIconComponent(FiMapPin);
const ArrowIcon = asIconComponent(FiArrowUpRight);

const AccessSection: React.FC = () => {
  return (
    <section id="access" className="pt-12 pb-16 md:pt-20 md:pb-24 bg-white">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-10 md:mb-12">
          <h3 className="text-2xl md:text-4xl font-bold text-gray-900 tracking-tight mb-2">
            店舗アクセス
          </h3>
        </div>

        <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
          {/* Info Side */}
          <div className="w-full md:w-1/2 space-y-6">
            {/* Address */}
            <div className="flex items-start gap-3">
              <div className="mt-1 text-gray-400">
                <MapPinIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-gray-900 text-base leading-relaxed">
                  〒{SHOP_INFO.address.zip}
                  <br />
                  {SHOP_INFO.address.line1}
                  {SHOP_INFO.address.line2 && (
                    <>
                      <br />
                      {SHOP_INFO.address.line2}
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start gap-3">
              <div className="mt-1 text-gray-400">
                <PhoneIcon className="w-5 h-5" />
              </div>
              <div>
                <a
                  href={`tel:${SHOP_INFO.phone}`}
                  className="text-lg md:text-xl font-bold text-gray-900 hover:text-[var(--temomi-red)] transition-colors font-feature-settings-tnum"
                >
                  {SHOP_INFO.phone}
                </a>
              </div>
            </div>

            {/* Hours */}
            <div className="flex items-start gap-3">
              <div className="mt-1 text-gray-400">
                <ClockIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-gray-900 text-base font-medium">
                  {SHOP_INFO.hours}
                </p>
              </div>
            </div>

            <div className="pt-4 flex justify-center">
              <a
                href={SHOP_INFO.reservationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-[var(--temomi-red)] text-white font-bold shadow-md hover:bg-red-700 hover:shadow-lg transition-all duration-300 text-base md:text-lg"
              >
                WEB予約する
                <ArrowIcon className="w-5 h-5 ml-2" />
              </a>
            </div>
          </div>

          {/* Map Side */}
          <div className="w-full md:w-1/2 h-[250px] md:h-[350px] bg-gray-100 rounded-xl overflow-hidden shadow-sm border border-gray-200">
            <iframe
              src={SHOP_INFO.mapUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen={true}
              loading="lazy"
              title="Google Map"
              className="grayscale-[0.5] hover:grayscale-0 transition-all duration-500"
            ></iframe>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AccessSection;
